import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import LoadingButton from '_shared/_views/loading/loading-button';
import Guid from '_shared/guids/guid';
import Password from 'users/auth/credentials/passwords/password';
import { enqueueSnackbar } from '_shared/_providers/snackbars/snackbars';
import React from 'react';
import { useSession } from 'users/__session/session-context';
import AuthEmailAPIService from 'users/_api/auth-email-api-service';
import ErrorList from '_shared/_errors/_views/error-list';
import PasswordSchema from 'users/auth/credentials/passwords/password-schema';
import { ZodError } from 'zod';

const PasswordInput = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.09)",
    color: theme.palette.common.white,
  },
  "& .MuiFilledInput-root:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.13)",
    // Reset on touch devices, it doesn't add specificity
    "@media (hover: none)": {
      backgroundColor: "rgba(255, 255, 255, 0.09)"
    }
  },
  "& .MuiFilledInput-root.Mui-focused": {
    backgroundColor: "rgba(255, 255, 255, 0.09)"
  },
  "& .MuiFilledInput-input:-webkit-autofill": {
    caretColor: theme.palette.common.white,
    WebkitBoxShadow: "0 0 0 100px #266798 inset",
    WebkitTextFillColor: theme.palette.common.white,
  },
  "& .MuiFilledInput-underline:before": {
    borderColor: theme.palette.common.white
  },
  "& .MuiFilledInput-underline:after": {
    borderColor: theme.palette.common.white
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)"
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.palette.common.white
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255, 255, 255, 0.7)"
  }
}));

const SubmitButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: '#E19941',
  margin: '1.5em 0',
  '&:hover': {
    backgroundColor: '#FFB949',
  },
  "&.MuiButton-root": {
    color: theme.palette.common.black
  },
  "&.Mui-disabled": {
    color: "rgba(255, 255, 255, 0.3)",
    boxShadow: "none",
    backgroundColor: "rgba(255, 255, 255, 0.12)"
  }
}));

interface Props {
  userId: string | null;
  token: string | null;
  onPasswordReset?: (password: string) => Promise<void>;
}

interface PasswordFormValues {
  password: string;
  passwordConfirm: string;
}

export default function PasswordResetForm(props: Readonly<Props>) {
  const [password, setPassword] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [resetting, setResetting] = React.useState(false);
  const [resetFailed, setResetFailed] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);
  const [formValid, setFormValid] = React.useState(false);

  const session = useSession();

  function getFormValues(form: HTMLFormElement): PasswordFormValues {
    const formData = new FormData(form);

    return {
      password: `${formData.get('password') ?? ''}`,
      passwordConfirm: `${formData.get('passwordConfirm') ?? ''}`,
    };
  }

  async function handleResetSubmitted(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();

    const values = getFormValues(event.currentTarget);

    setResetFailed(false);
    setErrors([]);
    setPassword(values.password);
    setPasswordConfirm(values.passwordConfirm);

    if (!validateForm(values)) return;

    setResetting(true);

    try {
      const { userId, token } = props;
      if (!userId || !Guid.isValid(userId) || !token) throw new Error('Invalid user ID and/or token');
      const authResponse = await AuthEmailAPIService.confirmPasswordReset(new Password(values.password, values.passwordConfirm), new Guid(userId), token);
      session.initializeFromAuthResponse(authResponse);
      if (props.onPasswordReset) 
        await props.onPasswordReset(values.password);
      enqueueSnackbar("Password update confirmed", { variant: 'success' });
    }
    catch {
      enqueueSnackbar("Password update failed", { variant: 'error' });
    } finally {
      setResetting(false);
    }
  }

  function handlePasswordChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const nextPassword = event.target.value;
    setPassword(nextPassword);
    validateForm({ password: nextPassword, passwordConfirm });
  }

  function handlePasswordConfirmChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const nextPasswordConfirm = event.target.value;
    setPasswordConfirm(nextPasswordConfirm);
    validateForm({ password, passwordConfirm: nextPasswordConfirm });
  }

  function validateForm(values: PasswordFormValues): boolean {
    setErrors([]);
    try {
      PasswordSchema.validate({ password: values.password, confirmPassword: values.passwordConfirm });
      setFormValid(true);
      return true;
    }
    catch (error) {
      if (error instanceof Error && error.cause instanceof ZodError)
      {
        setErrors(error.cause.issues.map(i => i.message));
        setFormValid(false);
        return false;
      }
      throw error;
    }
  }

  return (
    <Box component="form" noValidate onSubmit={handleResetSubmitted}>
      <PasswordInput
        variant="filled"
        size="small"
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type="password"
        id="password"
        error={!formValid || resetFailed}
        onChange={handlePasswordChanged}
        value={password}
        autoComplete="new-password"
        helperText="Min 8 characters. Requires at least (1) uppercase, lowercase, number &amp; symbol"
      />
      <PasswordInput
        variant="filled"
        size="small"
        margin="normal"
        required
        fullWidth
        name="passwordConfirm"
        label="Confirm Password"
        type="password"
        id="passwordConfirm"
        error={!formValid || resetFailed}
        onChange={handlePasswordConfirmChanged}
        value={passwordConfirm}
        autoComplete="new-password"
      />
      <ErrorList errors={errors} />
      <SubmitButton
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        disabled={!formValid || resetting}
        loading={resetting}>
        {resetting ? 'Creating Password...' : 'Set Password'}
      </SubmitButton>
    </Box>
  )
}
